import { Injectable } from '@angular/core';
import { IUserAttributes, IUserProfile, IUserProfileLoader } from 'ngx-register-base';
// eslint-disable-next-line sonarjs/deprecation
import { KeycloakEventTypeLegacy, KeycloakOptions, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, Observable, filter, from } from 'rxjs';
import { getErrorMsg } from '../utils/error-handling';

@Injectable({ providedIn: 'root' })
export class UserProfileService implements IUserProfileLoader<IUserProfile, any> {
  private _isLoggedIn$ = new BehaviorSubject(false);
  private _token$ = new BehaviorSubject('');
  private _userProfile$ = new BehaviorSubject<IUserProfile | undefined>(undefined);

  // eslint-disable-next-line sonarjs/deprecation
  constructor(private keycloakService: KeycloakService) {
    from(this.keycloakService.keycloakEvents$)
      // eslint-disable-next-line sonarjs/deprecation
      .pipe(filter((event) => event.type === KeycloakEventTypeLegacy.OnTokenExpired))
      .subscribe(() => {
        this.updateTokenHandler();
      });
  }
  // eslint-disable-next-line sonarjs/deprecation
  public async initKeycloak(initOptions: KeycloakOptions): Promise<void> {
    try {
      console.log('keycloak init start');
      const init = await this.keycloakService.init(initOptions);

      if (init) {
        const userProfile = await this._loadUserProfile();
        this.setUserProfile(userProfile);

        this.keycloakService.getKeycloakInstance().onTokenExpired = this.updateTokenHandler;
        this._isLoggedIn$.next(true);
      }

      console.log(`logged in: ${String(this._isLoggedIn$.getValue())}`);
    } catch (error) {
      console.error(getErrorMsg(error));
    }
  }

  protected updateTokenHandler: () => void = () => {
    this.keycloakService
      .updateToken(-1)
      .then(() => this.setTokenToSubjectFromKeycloak())
      .catch(() => this.logout());
  };

  get token$(): Observable<string> {
    return this._token$.asObservable();
  }

  get isLoggedIn(): Observable<boolean> {
    return this._isLoggedIn$.asObservable();
  }

  get getUserId(): string {
    return this.getUserProfile().id;
  }

  public getUserProfile(): IUserProfile {
    return <IUserProfile>this._userProfile$.getValue();
  }

  private setUserProfile(userProfile: IUserProfile): void {
    this._userProfile$.next(userProfile);
  }

  public getToken(): string {
    return this._token$.getValue();
  }

  public getTokenByKeycloakInstance(): string {
    return this.keycloakService.getKeycloakInstance().token ?? '';
  }

  public setTokenToSubjectFromKeycloak(): void {
    const { token } = this.keycloakService.getKeycloakInstance();
    if (token) {
      this._token$.next(token);
    }
  }

  public logout(): void {
    this.keycloakService
      .logout()
      .then((r) => {
        this._token$.next('');
        this._userProfile$.next({ id: '', token: '', username: '', roles: [] });
      })
      .catch((error) => {
        console.error(getErrorMsg(error));
      });
  }

  public checkPermissions(permissions: any[], every?: boolean): boolean {
    const userPermissions = this.getUserProfile()?.permissions ?? [];
    if (every) {
      return permissions.every((permission) => userPermissions.includes(permission));
    }

    return permissions.some((permission) => userPermissions.includes(permission));
  }

  public async _loadUserProfile(): Promise<IUserProfile> {
    const [profile, userInfo, token] = await Promise.all([
      this.keycloakService.loadUserProfile(),
      this.keycloakService.getKeycloakInstance().loadUserInfo(),
      this.keycloakService.getToken(),
    ]);

    const keycloakInstance = this.keycloakService.getKeycloakInstance();
    const roles: string[] = keycloakInstance.realmAccess ? keycloakInstance.realmAccess.roles : [];
    this._token$.next(token);

    const userAttributes: IUserAttributes[] = [];
    if (profile.attributes) {
      for (const [key, value] of Object.entries(profile.attributes)) {
        const attribute: IUserAttributes = {
          name: key,
          value: value as string,
        };
        userAttributes.push(attribute);
      }
    }

    const userProfile: IUserProfile = {
      id: (userInfo as any).sub,
      firstName: profile.firstName,
      lastName: profile.lastName,
      username: profile.username,
      roles,
      // группы - массив id назначенных групп (id_subsidiary). Название поля соответствует маппингу в клиенте кейклоак
      groups:
        ((userInfo as any).hasura_jwt_claims &&
          (userInfo as any).hasura_jwt_claims['x-hasura-groups']) ??
        [],
      groups_name: (userInfo as any).groups,
      token,
      permissions: (userInfo as any)['allowed-permissions'],
      attributes: userAttributes,
    };

    console.log('profile', userProfile);

    return userProfile;
  }
}
